import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useBankAccountStore } from '@/stores/bank-account.store';
import type { BankAccountInList } from '@/_helpers/interfaces/bank-accounts/bank-account-in-list.interface';

/**
 * Detects when an IBAN is already assigned to a different supplier under the
 * same customer. The same IBAN may legitimately be shared across suppliers
 * (a lessor operating under several supplier identities), so this is a
 * non-blocking warning: the user confirms before the account is saved.
 */
export function useSupplierBankAccountConflict() {
  const { t } = useI18n();
  const { findBankAccountsByIban } = useBankAccountStore();

  const showConflictModal = ref(false);
  const conflictMessage = ref('');

  async function findConflicts(
    iban: string,
    customerId: number,
    supplierId?: number | null,
    excludeBankAccountId?: number | null
  ): Promise<BankAccountInList[]> {
    if (!iban || !customerId) return [];

    // The backend stores IBANs normalized (no whitespace, upper-case); match
    // that here so a formatted input still finds existing accounts.
    const normalizedIban = iban.replace(/\s+/g, '').toUpperCase();
    const accounts = await findBankAccountsByIban(normalizedIban, customerId);
    return accounts.filter(
      account =>
        account.iban === normalizedIban &&
        account.supplier != null &&
        account.supplier !== supplierId &&
        account.id !== excludeBankAccountId
    );
  }

  function buildConflictMessage(conflicts: BankAccountInList[]): string {
    const suppliers = [
      ...new Set(conflicts.map(conflict => conflict.supplier_name).filter(Boolean)),
    ].join(', ');

    return t('suppliers.titles.shared_iban_warning', { suppliers });
  }

  return {
    showConflictModal,
    conflictMessage,
    findConflicts,
    buildConflictMessage,
  };
}
